import React, { useState } from "react";
import { Button, Modal, Slider, message, Spin } from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import Cropper from "react-easy-crop";
import axios from "axios";
import { getCroppedImageBlob } from "../../utils/cropImage";

// Generic image-picker with a crop step before uploading straight to S3 via
// a presigned URL - same two-step flow MediaForm.jsx uses for media files,
// just wrapped with a crop UI in between. `getSignedUrlEndpoint` is the
// full API path (e.g. "/api/inventory/get-signed-url").
const ImageCropUpload = ({ value, onChange, getSignedUrlEndpoint, aspect = 1, label = "Image" }) => {
  const [rawImage, setRawImage] = useState(null);
  const [rawFile, setRawFile] = useState(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setRawFile(file);
    setRawImage(URL.createObjectURL(file));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setIsCropModalOpen(true);
    e.target.value = "";
  };

  const onCropComplete = (_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  };

  const uploadBlobToS3 = async (blob, fileName, fileType) => {
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_URL}${getSignedUrlEndpoint}`,
      { params: { fileName, fileType } }
    );
    const { signedUrl, fileKey, fileUrl } = response.data;
    const uploadResponse = await fetch(signedUrl, {
      method: "PUT",
      body: blob,
      headers: { "Content-Type": fileType },
    });
    if (!uploadResponse.ok) throw new Error("Failed to upload to S3");
    return { file_key: fileKey, file_path: fileUrl };
  };

  const handleConfirmCrop = async () => {
    if (!croppedAreaPixels || !rawFile) return;
    try {
      setUploading(true);
      const blob = await getCroppedImageBlob(rawImage, croppedAreaPixels, rawFile.type);
      const uploaded = await uploadBlobToS3(blob, rawFile.name, rawFile.type);
      onChange(uploaded);
      setIsCropModalOpen(false);
      setRawImage(null);
      setRawFile(null);
    } catch (error) {
      console.error("Image upload failed:", error);
      message.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    onChange(null);
  };

  return (
    <div>
      {value?.file_path ? (
        <div className="d-flex align-items-center gap-2">
          <img
            src={value.file_path}
            alt={label}
            style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 4 }}
          />
          <Button danger size="small" icon={<DeleteOutlined />} onClick={handleRemove}>
            Remove
          </Button>
        </div>
      ) : (
        <Button icon={<UploadOutlined />} onClick={() => document.getElementById(`crop-upload-${label}`).click()}>
          Upload {label}
        </Button>
      )}
      <input
        id={`crop-upload-${label}`}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      <Modal
        title={`Crop ${label}`}
        open={isCropModalOpen}
        onCancel={() => setIsCropModalOpen(false)}
        maskClosable={false}
        footer={[
          <Button key="cancel" onClick={() => setIsCropModalOpen(false)} disabled={uploading}>
            Cancel
          </Button>,
          <Button key="confirm" type="primary" onClick={handleConfirmCrop} loading={uploading}>
            Crop & Upload
          </Button>,
        ]}
      >
        <Spin spinning={uploading}>
          <div style={{ position: "relative", width: "100%", height: 300, background: "#333" }}>
            {rawImage && (
              <Cropper
                image={rawImage}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          <div className="mt-3">
            <label className="form-label">Zoom</label>
            <Slider min={1} max={3} step={0.1} value={zoom} onChange={setZoom} />
          </div>
        </Spin>
      </Modal>
    </div>
  );
};

export default ImageCropUpload;
